/**
 * SLA Monitor Service — automatic SLA breach detection and warning emails.
 *
 * Periodically checks all in-progress and paused files for SLA breaches.
 * Sends warning emails when the 3-day active processing threshold is exceeded.
 * Tracks which files have already triggered warnings to avoid duplicate alerts.
 */

import { getAllFiles, updateFile } from './filesApi';
import { sendSLAWarning } from './mailApi';
import { calculateActiveDays, isSLABreached } from '../utils/slaCalculator';

// LocalStorage key for tracking which files have had SLA warnings sent
const SLA_WARNED_KEY = 'inbox_sla_warned';

/**
 * Get the set of file IDs that have already had SLA warnings sent.
 */
function getWarnedFiles() {
  try {
    const data = localStorage.getItem(SLA_WARNED_KEY);
    return data ? new Set(JSON.parse(data)) : new Set();
  } catch {
    return new Set();
  }
}

/**
 * Mark a file as having had its SLA warning sent.
 */
function markFileWarned(fileId) {
  const warned = getWarnedFiles();
  warned.add(fileId);
  localStorage.setItem(SLA_WARNED_KEY, JSON.stringify([...warned]));
}

/**
 * Run an SLA check across all active files.
 *
 * For each file that has breached SLA and hasn't been warned yet:
 * 1. Send warning emails to both Marinus and Wouter
 * 2. Update the file's slaBreached flag in the database
 * 3. Mark the file as warned to prevent duplicate emails
 *
 * @returns {Promise<{checked: number, breached: number, warned: number}>}
 */
export async function runSLACheck() {
  const results = { checked: 0, breached: 0, warned: 0 };

  try {
    const files = await getAllFiles();
    const warnedFiles = getWarnedFiles();

    // Check files that are actively being processed. Test records
    // (is_test = true, CLAUDE.md §33) are excluded entirely — no breach
    // flag is written and no warning email is ever sent for them.
    const activeFiles = files.filter((f) =>
      ['in_progress', 'paused'].includes(f.status) && f.isTest !== true
    );

    results.checked = activeFiles.length;

    for (const file of activeFiles) {
      const breached = isSLABreached(file);

      if (breached) {
        results.breached++;

        // Update the file's slaBreached flag if not already set
        if (!file.slaBreached) {
          try {
            await updateFile(file.id, { slaBreached: true });
          } catch (err) {
            console.warn(`[SLA] Failed to update breach flag for ${file.clientName}:`, err);
          }
        }

        // Send warning email if not already sent for this file
        if (!warnedFiles.has(file.id)) {
          try {
            const activeDays = calculateActiveDays(file);
            await sendSLAWarning(file, activeDays);
            markFileWarned(file.id);
            results.warned++;
            console.log(`[SLA] Warning sent for: ${file.clientName} (${activeDays.toFixed(1)} active days)`);
          } catch (err) {
            console.warn(`[SLA] Failed to send warning for ${file.clientName}:`, err);
          }
        }
      }
    }
  } catch (err) {
    console.error('[SLA] Check failed:', err);
  }

  return results;
}

/**
 * Check if a specific file is approaching SLA breach (warning zone).
 * Returns true if active time is between 2 and 3 days.
 *
 * @param {object} file
 * @returns {boolean}
 */
export function isApproachingSLA(file) {
  if (!file.drawnAt) return false;
  const days = calculateActiveDays(file);
  return days >= 2 && days < 3;
}

/**
 * Get a motivational message based on SLA status for the assistant.
 *
 * @param {object} file
 * @returns {string|null} message or null if no message needed
 */
export function getSLAMotivationalMessage(file) {
  if (!file.drawnAt) return null;
  const days = calculateActiveDays(file);

  if (days < 1) {
    return null; // Plenty of time, no message needed
  }
  if (days < 2) {
    return "Good pace — you're well within the 3-day target. Keep it going!";
  }
  if (days < 2.5) {
    return "Heads up — this file has been active for over 2 days. A little push and it's done! 💪";
  }
  if (days < 3) {
    return "⚠️ Approaching the 3-day SLA. Let's get this one across the line — you've got this!";
  }
  return "This file needs attention. Let's get it over the line — your bonus is counting on it. You've got what it takes.";
}

/**
 * Clear the warned-files cache. Used when resetting for a new month
 * or during testing.
 */
export function clearSLAWarnings() {
  localStorage.removeItem(SLA_WARNED_KEY);
}
